import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import AdminLayout from '../components/AdminLayout';
import {
  Eye, MessageSquare, Trash2, RefreshCw, Users, Clock, Search,
} from 'lucide-react';
import '../styles/leads.css';

const RoundtablePage = () => {
  const { token } = useAuth();
  const [activeTab, setActiveTab] = useState('views');
  const [views, setViews] = useState([]);
  const [comments, setComments] = useState([]);
  const [stats, setStats] = useState({ total_views: 0, total_comments: 0, unique_visitors: 0 });
  const [loading, setLoading] = useState(true);

  const API_URL = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => { fetchAll(); }, []);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const [sRes, vRes, cRes] = await Promise.all([
        fetch(`${API_URL}/api/roundtable/admin/stats`, { headers }),
        fetch(`${API_URL}/api/roundtable/admin/views`, { headers }),
        fetch(`${API_URL}/api/roundtable/admin/comments`, { headers }),
      ]);
      if (sRes.ok) setStats(await sRes.json());
      if (vRes.ok) { const d = await vRes.json(); setViews(d.views || []); }
      if (cRes.ok) { const d = await cRes.json(); setComments(d.comments || []); }
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  const handleDeleteComment = async (id) => {
    if (!window.confirm('Delete this comment?')) return;
    try {
      const res = await fetch(`${API_URL}/api/roundtable/admin/comments/${id}`, {
        method: 'DELETE', headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) { setComments(p => p.filter(c => c.id !== id)); fetchAll(); }
    } catch (e) { console.error(e); }
  };

  const seedBriefing = async () => {
    try {
      const res = await fetch(`${API_URL}/api/roundtable/admin/seed`, {
        method: 'POST', headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      alert(data.message);
    } catch (e) { alert('Seed failed'); }
  };

  const formatDate = (iso) => {
    if (!iso) return '-';
    return new Date(iso).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  return (
    <AdminLayout>
      <div data-testid="admin-roundtable-page" className="leads-page">
        <div className="leads-header">
          <div>
            <h2>Roundtable Briefings</h2>
            <p>Track views and comments on shared briefings</p>
          </div>
          <div className="leads-actions">
            <button data-testid="roundtable-seed-btn" className="action-btn" onClick={seedBriefing}>Seed Zambia Briefing</button>
            <button data-testid="roundtable-refresh-btn" className="action-btn primary" onClick={fetchAll}>
              <RefreshCw size={16} /> Refresh
            </button>
          </div>
        </div>

        {/* Stats */}
        <div className="leads-stats-row">
          <div className="lead-stat-card baride">
            <Eye size={22} />
            <div>
              <span className="stat-value" data-testid="roundtable-views-count">{stats.total_views}</span>
              <span className="stat-label">Total Views</span>
            </div>
          </div>
          <div className="lead-stat-card care365">
            <Users size={22} />
            <div>
              <span className="stat-value" data-testid="roundtable-visitors-count">{stats.unique_visitors}</span>
              <span className="stat-label">Unique Visitors</span>
            </div>
          </div>
          <div className="lead-stat-card total">
            <MessageSquare size={22} />
            <div>
              <span className="stat-value" data-testid="roundtable-comments-count">{stats.total_comments}</span>
              <span className="stat-label">Comments</span>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="leads-tabs">
          <button data-testid="roundtable-tab-views" className={`leads-tab ${activeTab === 'views' ? 'active' : ''}`} onClick={() => setActiveTab('views')}>
            <Eye size={16} /> View Log ({views.length})
          </button>
          <button data-testid="roundtable-tab-comments" className={`leads-tab ${activeTab === 'comments' ? 'active' : ''}`} onClick={() => setActiveTab('comments')}>
            <MessageSquare size={16} /> Comments ({comments.length})
          </button>
        </div>

        {/* Content */}
        <div className="leads-table-wrap">
          {loading ? (
            <div className="leads-loading"><div className="loading-spinner" /><p>Loading...</p></div>
          ) : activeTab === 'views' ? (
            views.length === 0 ? (
              <div className="leads-empty"><p>No views yet. Share the briefing URL to start tracking.</p></div>
            ) : (
              <table className="leads-table" data-testid="roundtable-views-table">
                <thead>
                  <tr>
                    <th>Visitor Name</th>
                    <th>Briefing</th>
                    <th>IP Hash</th>
                    <th>Viewed At</th>
                  </tr>
                </thead>
                <tbody>
                  {views.map((v) => (
                    <tr key={v.id}>
                      <td className="name-cell">{v.visitor_name || 'Anonymous'}</td>
                      <td>{v.briefing_slug}</td>
                      <td className="date-cell">{v.ip_hash || '-'}</td>
                      <td className="date-cell"><Clock size={12} className="inline mr-1" />{formatDate(v.viewed_at)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )
          ) : (
            comments.length === 0 ? (
              <div className="leads-empty"><p>No comments yet.</p></div>
            ) : (
              <table className="leads-table" data-testid="roundtable-comments-table">
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Comment</th>
                    <th>Project</th>
                    <th>Date</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {comments.map((c) => (
                    <tr key={c.id}>
                      <td className="name-cell">{c.commenter_name}</td>
                      <td style={{ whiteSpace: 'normal', maxWidth: '300px' }}>{c.comment}</td>
                      <td>{c.project_slug || 'General'}</td>
                      <td className="date-cell">{formatDate(c.created_at)}</td>
                      <td>
                        <button className="delete-btn" onClick={() => handleDeleteComment(c.id)} title="Delete">
                          <Trash2 size={15} />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )
          )}
        </div>
      </div>
    </AdminLayout>
  );
};

export default RoundtablePage;
